import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';

import { type PageIntroAction, usePageIntroActions } from '@/hooks/usePageIntroActions';

import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { ExpandableLede } from './ExpandableLede';
import { PageIntroActions } from './PageIntroActions';
import {
  BAND_EYEBROW_INK,
  BAND_LEDE_INK,
  BAND_TITLE_INK,
  INVERSE_ACCENT,
  INVERSE_LEDE,
  INVERSE_TITLE,
} from './pageIntroInk';

/**
 * How wide the headline is allowed to run. A ceiling, not the width.
 *
 * Which of the two actually bites depends on the band. On the cinematic one
 * the copy column is narrower than this cap — 22ch measures 838px against the
 * column's 668px at `lg` — so the column wins and the cap never applies; the
 * import page's headline sets on three lines there and would need roughly
 * 915px to set on two, which is width the car is using. This governs the
 * plain band, where the copy has no column to be held by, and it governs both
 * at the narrow end, where the clamp has shrunk the type enough for 22ch to
 * come back under the column.
 *
 * It was 18ch, which is a defensible measure for body copy and the wrong tool
 * here: display type is read in one glance rather than scanned line by line,
 * so the 45–75ch reading rule is not what is being satisfied. What 22ch buys
 * is the plain pages with long titles — "Importing a car to Uganda, step by
 * step" and its neighbours — which were breaking a line earlier than the band
 * had any need of.
 *
 * The lede keeps its own, narrower measure. It is the only block here that is
 * actually *read*, so it is the only one the reading-measure rule applies to.
 */
const TITLE_MEASURE = '22ch';
const LEDE_MEASURE = '54ch';

export type PageIntroCopyProps = {
  eyebrow?: string;
  title: string;
  lede?: string;
  /** The trail above the eyebrow. The band renders it, so it cannot get the tone wrong. */
  crumbs?: readonly Crumb[];
  action?: PageIntroAction;
  whatsapp?: boolean;
  children?: ReactNode;
};

type Props = PageIntroCopyProps & {
  /**
   * The band is wearing its stage, which it does from `md` up — so this
   * chooses between the palette's own ink and the stated inverse pair, rather
   * than between light and dark outright. See pageIntroBand.ts for why the
   * band stops being a stage at all below that width.
   */
  stage: boolean;
  /** Tight, two-column heading used by shopping catalogues. */
  compact?: boolean;
  /** The compact feature header stays on its dark stage at every breakpoint. */
  solidStage?: boolean;
};

/**
 * The words half of the opening band, and the page's only h1.
 *
 * The order is the order the visitor needs it in: where they are, what this
 * page is, what it offers, and what to do about it. The proof points are not
 * in here at all — they are a rail across the band's floor, because four
 * blocks under one headline read as a hierarchy and six read as a queue.
 *
 * Spacing is stated per block rather than by a `Stack`. That is deliberate: it
 * keeps each gap answerable on its own, and the gaps here are not one rhythm —
 * the eyebrow sits tight under the trail because it labels the headline, while
 * the buttons stand well clear of the lede because they end the block. Every
 * one of them is smaller below `md`, where the band is a title block: the
 * rhythm of a composition and the rhythm of a header are not the same rhythm,
 * and 56px of air under a lede on a 390px screen is a tenth of the viewport.
 */
export function PageIntroCopy({
  eyebrow,
  title,
  lede,
  crumbs,
  action,
  whatsapp,
  stage,
  compact = false,
  solidStage = false,
  children,
}: Props) {
  const compactGrid = compact && !stage;
  const actions = usePageIntroActions({
    ...(action ? { action } : {}),
    ...(whatsapp ? { whatsapp } : {}),
    stage,
  });

  return (
    <Box
      sx={
        compactGrid
          ? {
              display: { xs: 'block', md: 'grid' },
              gridTemplateColumns: { md: 'minmax(0, 1.3fr) minmax(16rem, 0.7fr)' },
              columnGap: { md: 8 },
              alignItems: 'end',
            }
          : {}
      }
    >
      {crumbs && (
        <Box sx={compactGrid ? { gridColumn: { md: '1 / -1' } } : {}}>
          <Breadcrumbs
            items={crumbs}
            compact={compact}
            {...(stage ? { stage, ...(solidStage ? { solidStage: true } : {}) } : {})}
          />
        </Box>
      )}
      {eyebrow && (
        <Typography
          variant="overline"
          component="p"
          sx={[
            {
              mb: compact ? { xs: 1, md: 1.5 } : { xs: 2, md: 3 },
              color: stage ? (solidStage ? INVERSE_ACCENT : BAND_EYEBROW_INK) : 'primary.main',
              ...(compactGrid && { gridColumn: { md: 1 } }),
            },
            compact && {
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              '&::before': {
                content: '""',
                width: 16,
                height: 2,
                flex: '0 0 auto',
                bgcolor: 'primary.main',
              },
            },
          ]}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        variant="h1"
        sx={[
          { maxWidth: compact ? { xs: TITLE_MEASURE, md: '32ch' } : TITLE_MEASURE },
          compact && {
            ...(compactGrid && { gridColumn: { md: 1 } }),
            fontSize: { xs: '2rem', md: 'clamp(2.75rem, 4vw, 3.75rem)' },
          },
          !!stage && { color: solidStage ? INVERSE_TITLE : BAND_TITLE_INK },
        ]}
      >
        {title}
      </Typography>
      {lede && (
        <Box
          sx={[
            {
              mt: compact ? { xs: 1, md: compactGrid ? 0 : 1.5 } : { xs: 3, md: 4 },
            },
            compactGrid && {
              gridColumn: { md: 2 },
              gridRow: { md: '2 / span 2' },
              alignSelf: 'end',
            },
          ]}
        >
          <ExpandableLede
            text={lede}
            maxWidth={LEDE_MEASURE}
            color={solidStage ? INVERSE_LEDE : stage ? BAND_LEDE_INK : 'text.secondary'}
            {...(solidStage ? { inverse: true } : {})}
            compact={compact}
          />
        </Box>
      )}
      {!actions.empty && (
        <Box
          sx={[
            {
              mt: compact ? { xs: 3, md: 0 } : { xs: 5, md: 7 },
              gridColumn: compactGrid ? { md: '1 / -1' } : undefined,
            },
            // Hidden rather than absent, and the margin goes with it: the row
            // is one element at every width (the page is prerendered once), so
            // a phone that is not shown the buttons must not be charged for
            // the gap above them either.
            !actions.onPhone && { display: { xs: 'none', md: 'block' } },
          ]}
        >
          <PageIntroActions plan={actions} />
        </Box>
      )}
      {children && (
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            gridColumn: compactGrid ? { md: '1 / -1' } : undefined,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
